import { db } from '../../../../config/firebaseConfig.js';
import { doc, getDoc, getDocs, collection, updateDoc, setDoc } from 'firebase/firestore';

import { loadProject } from '../../../ProjectsAndSkills/Projects/utilities/projectsUtils.js';
import { loadSkill } from '../../../ProjectsAndSkills/Skills/utilities/skillsUtils.js';

const visibilityIndexRef = (userId) => doc(db, 'users', userId, 'publishing', 'visibility-index');
const publishedPortfoliosRef = (userId) => collection(db, 'users', userId, 'published-portfolios');

const createReferenceState = () => ({
  imageIds: new Set(),
  projectIds: new Set(),
  skillIds: new Set(),
});

const addId = (set, value) => {
  if (typeof value !== 'string') {
    return;
  }

  const trimmedValue = value.trim();
  if (trimmedValue) {
    set.add(trimmedValue);
  }
};

const collectNodeReferences = (node, state) => {
  if (!node) {
    return;
  }

  if (Array.isArray(node)) {
    node.forEach((item) => collectNodeReferences(item, state));
    return;
  }

  if (typeof node !== 'object') {
    return;
  }

  if (typeof node.imageId === 'string') {
    addId(state.imageIds, node.imageId);
  }

  if (typeof node.projectId === 'string') {
    addId(state.projectIds, node.projectId);
  }

  if (typeof node.project === 'string') {
    addId(state.projectIds, node.project);
  }

  if (Array.isArray(node.skillIds)) {
    node.skillIds.forEach((skillId) => addId(state.skillIds, skillId));
  }

  if (node.attributes && typeof node.attributes === 'object') {
    addId(state.imageIds, node.attributes['data-image-id']);
    addId(state.projectIds, node.attributes['data-project-id']);
    addId(state.skillIds, node.attributes['data-skill-id']);
    addId(state.skillIds, node.attributes['data-project-skill-id']);
  }

  if (node.type === 'carousel' && Array.isArray(node.images)) {
    node.images.forEach((image) => {
      if (typeof image === 'string') {
        addId(state.imageIds, image);
      } else {
        collectNodeReferences(image, state);
      }
    });
  }

  if (node.type === 'project' && Array.isArray(node.images)) {
    node.images.forEach((image) => collectNodeReferences(image, state));
  }

  if (Array.isArray(node.content)) {
    node.content.forEach((child) => collectNodeReferences(child, state));
  }

  if (Array.isArray(node.columns)) {
    node.columns.forEach((column) => collectNodeReferences(column, state));
  }

  Object.values(node).forEach((value) => {
    if (Array.isArray(value) || (value && typeof value === 'object')) {
      collectNodeReferences(value, state);
    }
  });
};

export const extractPortfolioReferences = (portfolioData) => {
  const state = createReferenceState();
  collectNodeReferences(portfolioData?.content ?? portfolioData, state);

  return {
    referencedImages: Array.from(state.imageIds),
    referencedProjects: Array.from(state.projectIds),
    referencedSkills: Array.from(state.skillIds),
  };
};

const extractProjectImageIds = (projectData) => {
  const imageIds = new Set();

  const images = Array.isArray(projectData?.images) ? projectData.images : [];
  images.forEach((image) => {
    if (typeof image === 'string') {
      addId(imageIds, image);
      return;
    }

    if (image && typeof image === 'object') {
      addId(imageIds, image.imageId);
    }
  });

  return Array.from(imageIds);
};

const extractProjectSkillIds = (projectData) => {
  const skillIds = new Set();
  (Array.isArray(projectData?.skillsUsed) ? projectData.skillsUsed : [])
    .forEach((skillId) => addId(skillIds, skillId));
  return Array.from(skillIds);
};

const loadPublishedPortfolios = async (userId) => {
  const querySnapshot = await getDocs(publishedPortfoliosRef(userId));
  return querySnapshot.docs.map((portfolioDoc) => ({
    id: portfolioDoc.id,
    ...portfolioDoc.data(),
  }));
};

const loadVisibilityIndex = async (userId) => {
  const indexSnap = await getDoc(visibilityIndexRef(userId));
  if (!indexSnap.exists()) {
    return {
      imageIds: [],
      projectIds: [],
      skillIds: [],
    };
  }

  const indexData = indexSnap.data();
  return {
    imageIds: Array.isArray(indexData?.imageIds) ? indexData.imageIds : [],
    projectIds: Array.isArray(indexData?.projectIds) ? indexData.projectIds : [],
    skillIds: Array.isArray(indexData?.skillIds) ? indexData.skillIds : [],
  };
};

const addToCountMap = (map, ids) => {
  ids.forEach((id) => {
    map.set(id, (map.get(id) || 0) + 1);
  });
};

const buildReferenceCounts = async (userId, portfolios) => {
  const imageCounts = new Map();
  const projectCounts = new Map();
  const skillCounts = new Map();
  const portfolioSummaries = [];

  const uniqueProjectIds = new Set();
  const uniqueSkillIds = new Set();

  portfolios.forEach((portfolio) => {
    const references = extractPortfolioReferences(portfolio);

    portfolioSummaries.push({
      id: portfolio.id,
      referencedImages: references.referencedImages,
      referencedProjects: references.referencedProjects,
      referencedSkills: references.referencedSkills,
    });

    addToCountMap(imageCounts, references.referencedImages);
    addToCountMap(projectCounts, references.referencedProjects);
    addToCountMap(skillCounts, references.referencedSkills);

    references.referencedProjects.forEach((projectId) => uniqueProjectIds.add(projectId));
    references.referencedSkills.forEach((skillId) => uniqueSkillIds.add(skillId));
  });

  const projectSnapshots = await Promise.all(
    Array.from(uniqueProjectIds).map(async (projectId) => {
      try {
        const projectData = await loadProject(userId, projectId);
        return { projectId, projectData };
      } catch (error) {
        console.error('Error loading project during visibility sync:', error.message);
        return { projectId, projectData: null };
      }
    })
  );

  const projectImageIdsByProject = new Map();
  const projectSkillIdsByProject = new Map();

  projectSnapshots.forEach(({ projectId, projectData }) => {
    const imageIds = extractProjectImageIds(projectData);
    projectImageIdsByProject.set(projectId, imageIds);
    const linkedSkillIds = extractProjectSkillIds(projectData);
    projectSkillIdsByProject.set(projectId, linkedSkillIds);
    linkedSkillIds.forEach((skillId) => uniqueSkillIds.add(skillId));
  });

  const skillIconImageIdsBySkill = new Map();
  const skillSnapshots = await Promise.all(Array.from(uniqueSkillIds).map(async (skillId) => {
    try { return { skillId, skillData: await loadSkill(userId, skillId) }; }
    catch (error) {
      console.error('Error loading skill during visibility sync:', error.message);
      return { skillId, skillData: null };
    }
  }));
  skillSnapshots.forEach(({ skillId, skillData }) => {
    const imageId = skillData?.icon?.type === 'image' ? skillData.icon.imageId : null;
    skillIconImageIdsBySkill.set(skillId, typeof imageId === 'string' && imageId.trim() ? [imageId.trim()] : []);
  });

  portfolios.forEach((portfolio, portfolioIndex) => {
    const portfolioSummary = portfolioSummaries[portfolioIndex];
    const portfolioImageIds = new Set(portfolioSummary.referencedImages);
    const portfolioSkillIds = new Set(portfolioSummary.referencedSkills);

    portfolioSummary.referencedProjects.forEach((projectId) => {
      const projectImageIds = projectImageIdsByProject.get(projectId) || [];
      projectImageIds.forEach((imageId) => portfolioImageIds.add(imageId));
      (projectSkillIdsByProject.get(projectId) || [])
        .forEach((skillId) => portfolioSkillIds.add(skillId));
    });
    portfolioSkillIds.forEach((skillId) => {
      (skillIconImageIdsBySkill.get(skillId) || []).forEach((imageId) => portfolioImageIds.add(imageId));
    });

    const newlyReferencedSkillIds = [...portfolioSkillIds].filter(
      (skillId) => !portfolioSummary.referencedSkills.includes(skillId)
    );
    addToCountMap(skillCounts, newlyReferencedSkillIds);
    portfolioSummary.referencedSkills = [...portfolioSkillIds];
    addToCountMap(imageCounts, Array.from(portfolioImageIds));
  });

  return {
    portfolios: portfolioSummaries,
    imageCounts,
    projectCounts,
    skillCounts,
  };
};

const updateAssetFlags = async (userId, counts, previousIds, assetCollectionName) => {
  const nextIds = Array.from(counts.keys());
  const nextIdSet = new Set(nextIds);
  const previousIdSet = new Set(previousIds);

  const idsToUpdate = new Set([...nextIdSet, ...previousIdSet]);

  await Promise.all(
    Array.from(idsToUpdate).map(async (assetId) => {
      try {
        const assetRef = doc(db, 'users', userId, assetCollectionName, assetId);
        const isPublished = nextIdSet.has(assetId);
        const publicRefsCount = counts.get(assetId) || 0;

        await updateDoc(assetRef, {
          isPublished,
          publicRefsCount,
          updatedAt: new Date(),
        });
      } catch (error) {
        console.error(`Error updating ${assetCollectionName.slice(0, -1)} visibility:`, error.message);
      }
    })
  );
};

export const syncPublishedAssetVisibility = async (userId) => {
  const publishedPortfolios = await loadPublishedPortfolios(userId);
  const previousIndex = await loadVisibilityIndex(userId);
  const counts = await buildReferenceCounts(userId, publishedPortfolios);

  const nextIndex = {
    imageIds: Array.from(counts.imageCounts.keys()),
    projectIds: Array.from(counts.projectCounts.keys()),
    skillIds: Array.from(counts.skillCounts.keys()),
    updatedAt: new Date(),
  };

  await Promise.all(
    publishedPortfolios.map(async (portfolio) => {
      const references = counts.portfolios.find((summary) => summary.id === portfolio.id);
      if (!references) {
        return;
      }

      try {
        await updateDoc(doc(db, 'users', userId, 'published-portfolios', portfolio.id), {
          referencedImages: references.referencedImages,
          referencedProjects: references.referencedProjects,
          referencedSkills: references.referencedSkills,
          updatedAt: new Date(),
        });
      } catch (error) {
        console.error('Error updating published portfolio references:', error.message);
      }
    })
  );

  await updateAssetFlags(userId, counts.imageCounts, previousIndex.imageIds, 'images');
  await updateAssetFlags(userId, counts.projectCounts, previousIndex.projectIds, 'projects');
  await updateAssetFlags(userId, counts.skillCounts, previousIndex.skillIds, 'skills');

  await setDoc(visibilityIndexRef(userId), nextIndex, { merge: true });

  return nextIndex;
};
